
import React, { useRef, useState } from 'react';
import { Upload, Camera, Image as ImageIcon, Loader2, Award } from 'lucide-react';
import { Language } from '../types';
import { translations } from '../translations';

interface Props {
  onImagesSelect: (images: { base64: string; mimeType: string; label?: string }[]) => void;
  lang: Language;
}

export const ImageUploader: React.FC<Props> = ({ onImagesSelect, lang }) => {
  const t = translations[lang];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [selectedImages, setSelectedImages] = useState<{ base64: string; mimeType: string; label?: string }[]>([]);

  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        try {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1000;
          const MAX_HEIGHT = 1000;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error("Canvas context failed"));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.65));
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        console.error("Image load error details:", e);
        reject(new Error("Image load failed (图片加载失败，请检查格式或重试)"));
      };
      img.src = url;
    });
  };

  const processFile = async (file: File) => {
    const filename = file.name.toLowerCase();
    const isHeic = filename.endsWith('.heic') || filename.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif';
    
    if (!file.type.startsWith('image/') && !isHeic) {
      alert(lang === 'en' ? "Unsupported file format." : "不支持的文件格式。请上传图片文件。");
      return;
    }

    setIsConverting(true);

    try {
      let fileToRead = file;

      if (isHeic) {
        const heic2any = (window as { heic2any?: unknown }).heic2any as ((options: { blob: Blob; toType: string; quality: number }) => Promise<Blob | Blob[]>) | undefined;
        if (heic2any) {
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.7
            });
            const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            fileToRead = new File([blob], filename.replace(/\.(heic|heif)$/, ".jpg"), {
              type: "image/jpeg",
            });
          } catch (conversionError) {
            console.error("HEIC Conversion failed:", conversionError);
            alert(lang === 'zh' ? "HEIC 图片转换失败，请尝试截图后上传。" : "HEIC conversion failed. Please try uploading a screenshot.");
            setIsConverting(false);
            return;
          }
        } else {
          alert(lang === 'zh' ? "浏览器不支持 HEIC 格式，请尝试上传 JPG/PNG 格式。" : "HEIC not supported by browser. Please use JPG/PNG.");
          setIsConverting(false);
          return;
        }
      }

      try {
        // Resize and compress directly from File/Blob
        const resizedBase64 = await resizeImage(fileToRead);
        const finalMimeType = 'image/jpeg'; 
        const newImage = { base64: resizedBase64, mimeType: finalMimeType };
        setSelectedImages(prev => [...prev, newImage]);
        setIsConverting(false);
      } catch (err) {
        console.error("Processing error:", err);
        const msg = err instanceof Error ? err.message : "图片处理失败";
        alert(lang === 'zh' ? `图片处理失败: ${msg}` : `Image processing failed: ${msg}`);
        setIsConverting(false);
      }

    } catch (err) {
      console.error(err);
      setIsConverting(false);
    }
  };

  const handleLabelChange = (index: number, label: string) => {
    setSelectedImages(prev => prev.map((img, i) => i === index ? { ...img, label } : img));
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAnalyze = () => {
    if (selectedImages.length > 0) {
      onImagesSelect(selectedImages);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      Array.from(e.target.files).forEach(file => processFile(file));
    }
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      Array.from(e.dataTransfer.files).forEach(file => processFile(file));
    }
  };

  return (
    <div className="space-y-6">
      <div 
        className={`relative w-full max-w-2xl mx-auto transition-all duration-300 ease-in-out
          ${isDragging ? 'scale-[1.02] border-amber-500 bg-amber-50' : 'hover:border-stone-400 bg-white'}
          border-2 border-dashed border-stone-300 rounded-2xl shadow-sm overflow-hidden group cursor-pointer`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !isConverting && fileInputRef.current?.click()}
      >
        {isConverting && (
          <div className="absolute inset-0 bg-white/90 z-50 flex flex-col items-center justify-center backdrop-blur-[1px]">
            <Loader2 className="animate-spin text-amber-600 mb-3" size={36} />
            <p className="text-stone-600 font-serif-sc animate-pulse font-medium">{t.processing}</p>
          </div>
        )}

        <input type="file" ref={fileInputRef} onChange={handleChange} accept="image/*" multiple className="hidden" />
        <input type="file" ref={cameraInputRef} onChange={handleChange} accept="image/*" capture="environment" className="hidden" />
        
        <div className="p-10 md:p-12 text-center">
          <div className="flex justify-center mb-6 space-x-4">
            <div className="p-4 bg-stone-100 rounded-full text-stone-500 group-hover:bg-amber-100 group-hover:text-amber-600 transition-colors">
              <Upload size={32} />
            </div>
          </div>
          
          <h3 className="text-xl font-serif-sc text-stone-800 mb-2">{selectedImages.length > 0 ? t.addMore : t.multiUploadTitle}</h3>
          <p className="text-stone-500 mb-8 text-sm">{t.multiUploadDesc}</p>
          
          <div className="flex justify-center gap-4">
            <button onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} className="px-6 py-2 bg-stone-800 text-white rounded-full text-sm font-medium hover:bg-stone-700 transition-colors flex items-center gap-2 shadow-sm">
              <ImageIcon size={16} />{t.album}
            </button>
            <button onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }} className="px-6 py-2 bg-white border border-stone-300 text-stone-700 rounded-full text-sm font-medium hover:bg-stone-50 transition-colors flex items-center gap-2 shadow-sm">
              <Camera size={16} />{t.camera}
            </button>
          </div>
        </div>
      </div>

      {/* Expert Tips Guidance */}
      <div className="max-w-2xl mx-auto bg-amber-50/50 border border-amber-100 rounded-xl p-4 md:p-6 animate-fade-in">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-amber-100 rounded-lg text-amber-700">
            <Award size={18} />
          </div>
          <h4 className="font-serif-sc font-bold text-amber-900 text-sm md:text-base">{t.guideTitle}</h4>
        </div>
        <div className="space-y-3 text-xs md:text-sm text-amber-800/80 leading-relaxed">
          <p className="flex items-start gap-2">
            <span className="shrink-0 mt-0.5">💡</span>
            <span>{t.guideHand}</span>
          </p>
          <p className="flex items-start gap-2">
            <span className="shrink-0 mt-0.5">💡</span>
            <span>{t.guideThin}</span>
          </p>
        </div>
      </div>

      {selectedImages.length > 0 && (
        <div className="max-w-2xl mx-auto animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {selectedImages.map((img, index) => (
              <div key={index} className="bg-white p-3 rounded-xl border border-stone-200 shadow-sm relative group">
                <button 
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity z-10"
                >
                  <Upload size={12} className="rotate-45" />
                </button>
                <img src={img.base64} alt="Preview" className="w-full h-32 object-cover rounded-lg mb-3" />
                <select 
                  value={img.label || ''} 
                  onChange={(e) => handleLabelChange(index, e.target.value)}
                  className="w-full text-xs bg-stone-50 border border-stone-200 rounded-md p-2 outline-none focus:border-amber-500"
                >
                  <option value="">{lang === 'zh' ? '选择标注' : 'Select Label'}</option>
                  <option value="PPL">{t.pplLabel}</option>
                  <option value="XPL">{t.xplLabel}</option>
                  <option value="Macro">{t.macroLabel}</option>
                  <option value="Micro">{t.microLabel}</option>
                </select>
              </div>
            ))}
          </div>
          <button 
            onClick={handleAnalyze}
            className="w-full py-4 bg-gradient-to-r from-emerald-600 to-stone-700 text-white rounded-xl font-serif-sc font-bold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-3"
          >
            <Loader2 className={isConverting ? 'animate-spin' : 'hidden'} size={20} />
            {t.analyzeNow} ({selectedImages.length} {t.imageCount})
          </button>
        </div>
      )}
    </div>
  );
};
